// PresenceTracker - Enhanced invisible component for student presence
// Supports session modes (theoretical/practical/exam) with adaptive thresholds
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth-context';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PING_INTERVAL_MS = 60 * 1000;
const VERIFICATION_TIMEOUT_MS = 60 * 1000;

// Default idle thresholds per mode (frontend-side, in ms).
// These mirror the backend config for UI feedback.
const MODE_IDLE_MS = {
  theoretical: 5 * 60 * 1000,
  practical: 15 * 60 * 1000,
  exam: 3 * 60 * 1000,
};

export default function PresenceTracker({ sessionId, workspaceId }) {
  const { user } = useAuth();
  const [isIdle, setIsIdle] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCountdown, setVerificationCountdown] = useState(60);
  const [sessionMode, setSessionMode] = useState('theoretical');

  const lastInteractionRef = useRef(Date.now());
  const pingIntervalRef = useRef(null);
  const idleCheckRef = useRef(null);
  const verificationTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const token = localStorage.getItem('token');
  const isCandidate = user?.role === 'candidate';
  const idleThreshold = MODE_IDLE_MS[sessionMode] || MODE_IDLE_MS.theoretical;

  // ─── Track user activity ───
  const handleActivity = useCallback(() => {
    lastInteractionRef.current = Date.now();
    if (isIdle) setIsIdle(false);
  }, [isIdle]);

  useEffect(() => {
    if (!isCandidate) return;
    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    let throttleTimer = null;
    const throttledHandler = () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          handleActivity();
          throttleTimer = null;
        }, 3000);
      }
    };
    events.forEach(e => window.addEventListener(e, throttledHandler, { passive: true }));
    return () => {
      events.forEach(e => window.removeEventListener(e, throttledHandler));
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [handleActivity, isCandidate]);

  // ─── Check idle state (adaptive to session mode) ───
  useEffect(() => {
    if (!isCandidate) return;
    idleCheckRef.current = setInterval(() => {
      const timeSince = Date.now() - lastInteractionRef.current;
      setIsIdle(timeSince > idleThreshold);
    }, 10000);
    return () => clearInterval(idleCheckRef.current);
  }, [isCandidate, idleThreshold]);

  // ─── Send verification result ───
  const sendVerificationResult = useCallback(async (success) => {
    try {
      await fetch(`${API_URL}/api/presence/ping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          session_id: sessionId,
          workspace_id: workspaceId,
          status: 'active',
          verification_response: success,
        }),
      });
    } catch (err) {
      console.error('[PresenceTracker] Verification error:', err);
    }
  }, [token, sessionId, workspaceId]);

  // ─── Verification popup ───
  const triggerVerification = useCallback(() => {
    setShowVerification(true);
    setVerificationCountdown(60);
    countdownRef.current = setInterval(() => {
      setVerificationCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setShowVerification(false);
          sendVerificationResult(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    verificationTimerRef.current = setTimeout(() => {
      clearInterval(countdownRef.current);
      setShowVerification(false);
    }, VERIFICATION_TIMEOUT_MS + 2000);
  }, [sendVerificationResult]);

  // ─── Ping backend periodically ───
  useEffect(() => {
    if (!isCandidate || !sessionId || !workspaceId || !token) return;

    const sendPing = async () => {
      try {
        const res = await fetch(`${API_URL}/api/presence/ping`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            session_id: sessionId,
            workspace_id: workspaceId,
            status: isIdle ? 'idle' : 'active',
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.session_mode) setSessionMode(data.session_mode);
          if (data.request_verification && !showVerification) triggerVerification();
        }
      } catch (err) {
        console.error('[PresenceTracker] Ping error:', err);
      }
    };

    sendPing();
    pingIntervalRef.current = setInterval(sendPing, PING_INTERVAL_MS);
    return () => clearInterval(pingIntervalRef.current);
  }, [sessionId, workspaceId, token, isIdle, isCandidate, showVerification, triggerVerification]);

  const handleVerificationConfirm = () => {
    clearInterval(countdownRef.current);
    clearTimeout(verificationTimerRef.current);
    setShowVerification(false);
    sendVerificationResult(true);
    lastInteractionRef.current = Date.now();
    setIsIdle(false);
  };

  useEffect(() => {
    return () => {
      clearInterval(pingIntervalRef.current);
      clearInterval(idleCheckRef.current);
      clearInterval(countdownRef.current);
      clearTimeout(verificationTimerRef.current);
    };
  }, []);

  if (!isCandidate || !sessionId || !workspaceId) return null;

  return (
    <>
      {showVerification && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999, backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e1b4b, #312e81)',
            borderRadius: '20px', padding: '40px', maxWidth: '420px', width: '90%',
            textAlign: 'center', boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
            color: 'white', animation: 'slideIn 0.3s ease-out',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>👋</div>
            <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>
              Êtes-vous toujours là ?
            </h2>
            <p style={{ fontSize: '14px', opacity: 0.8, marginBottom: '24px' }}>
              {sessionMode === 'practical'
                ? "Si vous travaillez sur un outil externe, confirmez votre présence."
                : "Veuillez confirmer votre présence pour continuer à suivre le cours."}
            </p>
            <div style={{
              width: '80px', height: '80px', margin: '0 auto 24px',
              borderRadius: '50%', border: '4px solid rgba(255,255,255,0.2)',
              borderTopColor: verificationCountdown > 10 ? '#22c55e' : '#ef4444',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '28px', fontWeight: '700', transition: 'border-top-color 0.3s',
            }}>
              {verificationCountdown}
            </div>
            <button
              onClick={handleVerificationConfirm}
              style={{
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                color: 'white', border: 'none', padding: '14px 40px',
                borderRadius: '50px', fontSize: '16px', fontWeight: '700',
                cursor: 'pointer', boxShadow: '0 4px 15px rgba(34,197,94,0.4)',
                transition: 'transform 0.2s',
              }}
              onMouseOver={(e) => { e.target.style.transform = 'scale(1.05)'; }}
              onMouseOut={(e) => { e.target.style.transform = 'scale(1)'; }}
            >
              ✅ Je suis là !
            </button>
          </div>
        </div>
      )}
      <style>{`
        @keyframes slideIn {
          from { transform: translateY(-30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}
